import {Injectable} from '@angular/core';
import {environment} from "../../../environments/environment";
import {Observable} from "rxjs";
import {HttpService} from "../http.service";
import {CreateEventRequest} from "../../shared/model/request/create-event-request.model";
import {EventResponse} from "../../shared/model/response/event/event-response.model";
import {ApiResponse} from "../../shared/model/response/ApiResponse";
import {AuthtokenResponse} from "../../shared/model/response/auth/authtoken-response.model";
import {FilePondFile} from "filepond";
import {HttpUtils} from "../../shared/utils/http.utils";
import {EventFilterModel} from "../../shared/model/request/event-filter.model";
import {DatePipe} from "@angular/common";

@Injectable({
  providedIn: 'root'
})
export class EventService {

  private version = "/api/v1/"
  private host = environment.host + this.version
  private baseurl: string = this.host + "events"

  constructor(private http: HttpService, private datePipe: DatePipe) {
  }

  getEventBySlug(slug: string): Observable<EventResponse> {
    return this.http.get(this.baseurl.concat("/").concat(slug), {shouldShowLoading: true});
  }

  /**
   * retrieve list of events
   * @param page page
   * @param filters
   */
  getEvents(page: number = 0, filters: EventFilterModel): Observable<ApiResponse<EventResponse[]>> {

    //convert date fields
    if (filters.eventDate) {
      filters.eventDate = this.datePipe.transform(filters.eventDate, 'yyyy-MM-dd');
    }

    let params = HttpUtils.convertObjectToParam(filters).append("page", page)

    return this.http.get(this.baseurl, {params, shouldShowLoading: true});
  }

  deleteEventImage(eventId: number): Observable<ApiResponse<EventResponse>> {
    return this.http.delete(this.baseurl + "/" + eventId + "/image", {shouldShowLoading: true})
  }

  //endpoint to move event from pending to either active or adjustment
  activateEvent(eventId: number, decision: {
    decision: boolean,
    motive: string
  }): Observable<ApiResponse<EventResponse>> {
    let requestBody = {eventId: eventId, ...decision}
    return this.http.put(this.baseurl + "/activate", requestBody)
  }

  terminateEvent(eventId: number, isResolved: boolean): Observable<ApiResponse<EventResponse>> {
    let requestBody = {eventId, isResolved}
    return this.http.put(this.baseurl + "/terminate", requestBody)
  }

  deleteAccountEvents(req: {verification: string}) {
    return this.http.put(this.baseurl + "/user/delete-all", req);
  }

  transferAccountEvents(req: {email: string}) {
    return this.http.put(this.baseurl + "/user/transfer-all", req)
  }

  /**
   * remove/delete an event
   * @param eventId
   * @param reason
   */
  removeEvent(eventId: number, reason: string): Observable<ApiResponse<EventResponse>> {
    return this.http.put(this.baseurl.concat("/",eventId.toString(),"/delete"), {reason})
  }

  /**
   * allow the item to be restored before permanent remove/delete an event
   * @param eventId
   */
  restoreToRemoveEvent(eventId: number): Observable<ApiResponse<EventResponse>> {
    return this.http.put(this.baseurl.concat("/",eventId.toString(),"/delete-restore"))
  }

  /**
   * send event creation form data along with uploaded picture
   * @param createEventForm the form data
   * @param image event image if any
   *
   */
  saveEvent(createEventForm: CreateEventRequest, image?: FilePondFile, original?: File, redacted?: File): Observable<ApiResponse<EventResponse>> {

    const formData = new FormData();
    formData.set('eventData', JSON.stringify(createEventForm))


    if (redacted && original) {
      formData.append('originalImage', original, original.name);
      formData.append('blurredImage', redacted, redacted.name);
    } else if (image) {
      formData.append('originalImage', image.file, image.filename);
    }

    return this.http.post(this.baseurl, formData, {shouldShowLoading: true})
  }

  editEventData(id: number, body: CreateEventRequest): Observable<ApiResponse<EventResponse>> {
    return this.http.put(this.baseurl.concat("/").concat(String(id)), body)
  }

  editEventImage(id: number, image: FilePondFile, original?: File, redacted?: File): Observable<ApiResponse<any>> {

    const formData = new FormData();
    if (redacted && original) {
      formData.append('originalImage', original, original.name);
      formData.append('blurredImage', redacted, redacted.name);
    } else {
      formData.append('originalImage', image.file, image.filename);
    }

    return this.http.put(this.baseurl.concat("/").concat(String(id).concat("/image"),), formData, {shouldShowLoading: true})
  }

  //checks if the loggedin user is the event owner.
  isEventOwner(user: AuthtokenResponse, event: EventResponse) {
    if (!(user || event)) return false;
    return user?.id === event.account?.id
  }
}
